/* eslint-disable max-lines-per-function */
/*
 *--------------------------------------------------------------------------
 * Bloomreach
 *--------------------------------------------------------------------------
 *
 * This class is used to implement elements of the Bloomreach
 * JavaScript SDK integration.
 *
 * https://documentation.bloomreach.com/engagement/docs/js-sdk
 *
 */

import decodeEntities from '@mvps-genesis/decode-entities';
import EventBus from '@mvps-genesis/event-bus';
import ParameterValidator from '@mvps-genesis/parameter-validator';


export default class Bloomreach {

	/**
	 * Cart data attribute name
	 *
	 * @var	String
	 *
	 */

	cartAttributeName;

	/**
	 * The mini-basket container selector
	 *
	 * @var String
	 */
	containerSelector;

	/**
	 * Class options object
	 *
	 * @var Object
	 */
	options;

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/utilities	ParameterValidator
	 */
	#validator;

	/**
	 * Create a new Bloomreach object
	 *
	 * @param	String		options.customerEmail				The Bloomreach customer email. Defaults to empty.
	 * @param	String		options.customerID					The Bloomreach Miva customer ID. Defaults to 0.
	 * @param	String		options.cartAttributeName			The cart data attribute name. Defaults to 'data-bloomreach-cart'.
	 * @param	String		options.containerSelector			The mini-basket container selector. Defaults to '.js-mini-basket'.
	 */
	constructor (options = {}) {
		// Create param validator
		this.#validator = new ParameterValidator(this);

		// Store the provided options
		this.options = this.#validator.isObject('options', options);

		this.customerEmail = this.#validator.isString('options.customerEmail', this.options.customerEmail || '');
		this.customerID = this.#validator.isNumber('options.customerID', this.options.customerID || 0);
		this.cartAttributeName = this.#validator.isString('options.cartAttributeName', this.options.cartAttributeName || 'data-bloomreach-cart');
		this.containerSelector = this.#validator.isString('options.containerSelector', this.options.containerSelector || '.js-mini-basket');

		this.bloomreachURL = window.mivaJS?.bloomreach?.apiurl;

		this.init();
	}

	init () {
		// Bind events
		this.bindEvents();
		this.waitForExponeaAndIdentify();
		this.bloomreachTrackingGlobalFooter();
		this.bloomreachTrackingEmailForm();
	}

	waitForExponeaAndIdentify () {
		// Set-up Identity
		const observer = new MutationObserver((mutations, me) => {
			// Wait until Exponea had loaded
			if (window.exponea && typeof window.exponea.identify === 'function') {
				me.disconnect();

				// eslint-disable-next-line new-cap
				const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

				if (!timeZone) {
					window.exponea.update(timeZone ? {timezone: timeZone} : {});
					return;
				}

				/* eslint camelcase: ["error", {properties: "never"}] */
				window.exponea.identify(
					{
						...this.customerEmail ? {email_id: this.customerEmail} : {},
						...this.customerID ? {miva_id: this.customerID} : {}
					},
					{
						...this.customerEmail ? {email: this.customerEmail} : {},
						...timeZone ? {timezone: timeZone} : {}
					}
				);
			}
		});

		observer.observe(
			document,
			{
				childList: true,
				subtree: true
			}
		);

		/*
		 * In case exponea doesn't load, don't watch forever
		 */
		setTimeout(() => observer.disconnect(), 15000);
	}

	bindEvents () {
		// Listen for global event - `MVPS:addToCart` and handle response
		EventBus.addEventListener('MVPS:addToCart', this.onAddToCart.bind(this), false);

		EventBus.addEventListener('MVPS:productVariantChanged', this.onVariantChanged.bind(this), false);

		EventBus.addEventListener('MVPS:removeFromCart', this.onRemoveFromCart.bind(this), false);

		EventBus.addEventListener('MVPS:searchQuery', this.onSearchQuery.bind(this), false);

		EventBus.addEventListener('MVPS:addToWishlist', this.onAddToWishlist.bind(this), false);

		EventBus.addEventListener('MVPS:shopStoreProductChanged', this.onMyStoreSelected.bind(this), false);
		EventBus.addEventListener('MVPS:shopStoreSearchChanged', this.onMyStoreSelected.bind(this), false);
		EventBus.addEventListener('MVPS:shopStoreBasketChanged', this.onMyStoreSelected.bind(this), false);

		window.checkBloomreachConsent = this.returnBloomreachConsentStatus.bind(this);
	}

	onVariantChanged (event) {
		const variantData = event.detail.productData.variant;
		const eventProperties = window.mivaJS.BloomreachFields;

		let bopisStoreInventories = null;
		if (variantData.customfield_values.bopis_inventory) {
			bopisStoreInventories = Object.entries(JSON.parse(variantData.customfield_values.bopis_inventory)).map(([
				storeId,
				stockLevel
			]) => ({
				stock_level: Number(stockLevel),
				store_id: Number(storeId)
			}));
		}

		// Get the variant SKU value after the colon
		eventProperties.variant_id = variantData.sku.split(':')[1].trim();
		eventProperties.sku = variantData.sku;
		eventProperties.stock_level = variantData.inv_available;
		eventProperties.store_stock_level = bopisStoreInventories;

		const productProperties = {
			...window?.MivaJS?.bloomreachProperties,
			...eventProperties
		};

		window.exponea?.track('view_item', productProperties);
	}

	onAddToCart (event) {
		// Bloomreach Integration: Cart Activity
		if (event.detail && event.detail.success === true && typeof event.detail.data === 'object') {

			const miniBasket = event.detail.data.querySelector(this.containerSelector);
			const button = event.detail.form.querySelector('.js-add-to-cart');
			const buttonCopy = button.getAttribute('data-value') ?? 'Add to Cart';

			if (!miniBasket) {
				return false;
			}

			const cartAttributeElement = miniBasket.querySelector(`[${this.cartAttributeName}]`);

			if (!cartAttributeElement) {
				return false;
			}

			const cartData = JSON.parse(decodeEntities(cartAttributeElement.getAttribute(this.cartAttributeName)));

			if (!cartData || !cartData.length) {
				return false;
			}

			const bloomreachProduct = window.mivaJS?.BloomreachFields;

			bloomreachProduct.product_ids = cartData.map(item => item.parent_sku);
			bloomreachProduct.product_list = cartData.map(item => ({
				product_id: item.parent_sku,
				quantity: item.quantity
			}));

			// Calculate basket total quantity and total price
			bloomreachProduct.total_quantity = cartData.map(item => item.quantity).reduce((a, b) => a + b, 0);
			bloomreachProduct.total_price = cartData.map(item => item.price * item.quantity).reduce((a, b) => a + b, 0).toFixed(2);

			bloomreachProduct.variant_ids = cartData.map(item => item.sku);
			bloomreachProduct.variant_list = cartData.map(item => ({
				quantity: item.quantity,
				variant_id: item.sku
			}));

			const cartSession = miniBasket.dataset.session;
			const cartLink = cartSession ? `${window.mivaJS.Basket_Url}Session_ID=${cartSession}&restore_basket=1` : '';

			const eventProperties = {
				action: 'add',
				button_copy: buttonCopy,
				button_type: event.detail.bloomreach.button_type ?? 'generic',
				cart_link: cartLink,
				domain: window.location.hostname ?? '',
				location: window.location.href ?? '',
				page_type: window.mivaJS.Page_Code ?? '',
				title: document.title ?? ''
			};

			const bloomreachProperties = {...eventProperties,
				...bloomreachProduct};

			window.exponea?.track('cart_update', bloomreachProperties);
		}

		return true;
	}

	onRemoveFromCart (event) {
		const miniBasket = document.getElementById('js-mini-basket');

		const cartAttributeElement = miniBasket.querySelector(`[${this.cartAttributeName}]`);

		if (!cartAttributeElement) {
			this.emptyCart();
			return false;
		}

		const cartData = JSON.parse(decodeEntities(cartAttributeElement.getAttribute(this.cartAttributeName)));

		if (!cartData || !cartData.length) {
			return false;
		}

		const bloomreachBasket = {};
		bloomreachBasket.product_id = event.detail.target.dataset.bloomreachProduct_id ?? '';
		bloomreachBasket.product_ids = cartData.map(item => item.parent_sku);
		bloomreachBasket.product_list = cartData.map(item => ({
			product_id: item.parent_sku,
			quantity: item.quantity
		}));

		bloomreachBasket.variant_id = event.detail.target.dataset.bloomreachVariant_id ?? '';
		bloomreachBasket.variant_ids = cartData.map(item => item.variant_sku);
		bloomreachBasket.variant_list = cartData.map(item => ({
			quantity: item.quantity,
			variant_id: item.variant_sku
		}));

		// Calculate basket total quantity and total price
		bloomreachBasket.total_quantity = cartData.map(item => item.quantity).reduce((a, b) => a + b, 0);
		bloomreachBasket.total_price = cartData.map(item => item.price * item.quantity).reduce((a, b) => a + b, 0).toFixed(2);

		const cartSession = miniBasket.querySelector(this.containerSelector).dataset.session;
		const cartLink = cartSession ? `${window.mivaJS.Basket_Url}Session_ID=${cartSession}&restore_basket=1` : '';

		const eventProperties = {
			action: 'remove',
			button_copy: 'remove',
			button_type: 'remove product',
			cart_link: cartLink,
			domain: window.location.hostname ?? '',
			location: window.location.href ?? '',
			page_type: window.mivaJS.Page_Code ?? '',
			title: document.title ?? ''
		};

		const bloomreachProperties = {
			...eventProperties,
			...bloomreachBasket
		};

		window.exponea?.track('cart_update', bloomreachProperties);

		return true;
	}

	emptyCart () {
		const eventProperties = {
			action: 'empty',
			button_copy: 'Empty Cart',
			button_type: 'Remove Link',
			domain: window.location.hostname ?? '',
			location: window.location.href ?? '',
			page_type: window.mivaJS.Page_Code ?? '',
			product_ids: [],
			product_list: [],
			title: document.title ?? '',
			total_price: 0,
			total_quantity: 0,
			variant_ids: [],
			variant_list: []
		};

		window.exponea?.track('cart_update', eventProperties);
	}

	onSearchQuery (event) {
		if (!event?.detail?.query) {
			return;
		}

		const bloomreachProperties = {
			location: window.location.href,
			search_term: event?.detail?.query
		};

		window.exponea?.track('onsite_search', bloomreachProperties);
	}

	onAddToWishlist (event) {
		if (event.detail && event.detail.success && event.detail.form) {

			const bloomreachFields = window.mivaJS?.BloomreachFields;

			const eventProperties = {
				action: 'add',
				categories_path: bloomreachFields.categories_path ?? '',
				category_level_1: bloomreachFields.category_level_1 ?? '',
				category_level_2: bloomreachFields.category_level_2 ?? '',
				name: event.detail?.data?.wishlist_name ?? '',
				product_id: event.detail.form.getAttribute('data-gtm-sku') ?? '',
				variant_id: event.detail.form.getAttribute('data-gtm-variant') ?? ''
			};

			window.exponea?.track('wishlist', eventProperties);
		}
	}

	onMyStoreSelected (location) {
		// eslint-disable-next-line new-cap
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		/* eslint camelcase: ["error", {properties: "never"}] */
		window.exponea?.identify(
			{
				...this.customerEmail ? {email_id: this.customerEmail} : {},
				...this.customerID ? {miva_id: this.customerID} : {}
			},
			{
				...this.customerEmail ? {email: this.customerEmail} : {},
				...location.detail?.location?.identifier ? {last_selected_store: location.detail?.location?.identifier} : {},
				...timeZone ? {timezone: timeZone} : {}
			}
		);
	}

	bloomreachTrackingGlobalFooter () {
		// Global Footer Newsletter
		const globalFooterNewsletter = document.getElementById('js-footer-newsletter-form');
		const globalFooterNewsletterMessage = document.getElementById('js-footer-newsletter-form--message');
		if (!globalFooterNewsletter) {
			return;
		}

		const globalFooterNewsletterEmailInput = document.getElementById('l-Newsletter_Signup_Email');

		// eslint-disable-next-line max-lines-per-function
		globalFooterNewsletter.addEventListener('submit', async (e) => {
			e.preventDefault();

			const userEmail = globalFooterNewsletterEmailInput.value;

			if (await this.returnBloomreachConsentStatus(userEmail)) {
				globalFooterNewsletterMessage.innerHTML = `Great news, you've already signed up! You'll be redirected shortly!`;
			} else {
				if (globalFooterNewsletterEmailInput && globalFooterNewsletterEmailInput.value) {
					/* eslint-disable */
					window.dataLayer.push({
						namespace: 'exponea',
						event_name: 'consent',
						event_properties: {
							action : 'accept',
							category: 'email',
							data_source: 'footer',
							timestamp: Math.floor(Date.now() / 1000),
							valid_until: 'unlimited',
							location: window.location.href
						}
					});
					/* eslint-enable */

					// Identify user via the Value.
					if (window.exponea) {
						window.exponea.identify(
							{
								// eslint-disable-next-line camelcase
								email_id: globalFooterNewsletterEmailInput.value.toLowerCase().trim()
							},
							{
								email: globalFooterNewsletterEmailInput.value.toLowerCase().trim()
							}
						);
					}
				}

				globalFooterNewsletterMessage.innerHTML = `Thank you for signing up! You will be redirected shortly.`;
			}

			setTimeout(() => {
				globalFooterNewsletter.submit();
			}, 3000);
		});
	}

	async checkBloomreachConsent (hardID, type) {
		let bloomreachApiUrl = this.bloomreachURL;

		if (type === 'email') {
			bloomreachApiUrl = bloomreachApiUrl + 'Get=Check_Consent&hard_id=' + encodeURIComponent(hardID);
		} else if (type === 'sms') {
			bloomreachApiUrl = bloomreachApiUrl + 'Get=Check_Consent_Sms&hard_id=' + encodeURIComponent(hardID);
		}
		const response = await fetch(bloomreachApiUrl);

		if (!response.ok) {
			return false;
		}

		return response.json();
	}

	bloomreachTrackingEmailForm () {
		// Check for any Forms with "Opt in"
		const optIn = document.querySelector('input[name="SignUp_Checked"]');
		if (!optIn) {
			return;
		}

		const optInForm = optIn.form;
		if (!optInForm) {
			return;
		}

		const optInEmail = optInForm.querySelector('#l-Create_Customer_LoginEmail, #l-Customer_CreateEmail, #l-BillEmail');
		if (!optInEmail) {
			return;
		}

		optInForm.addEventListener('submit', async () => {

			const currentEmailConsent = await this.returnBloomreachConsentStatus(optInEmail.value);

			// Only update if they have changed consent options
			if (currentEmailConsent !== optIn.checked) {
				this.bloomreachTrackingOptin(optInForm, optIn.checked ? 'accept' : 'reject', optInEmail.value);
			}

			if (optInEmail.value && window.exponea) {
				window.exponea.identify(
					{
						// eslint-disable-next-line camelcase
						email_id: optInEmail.value.toLowerCase().trim()
					},
					{
						email: optInEmail.value.toLowerCase().trim()
					}
				);
			}
		});
	}

	async bloomreachTrackingOptin (form, action, email) {
		/* eslint no-extra-parens: ["error", "all", { "nestedBinaryExpressions": false }] */
		if (!email || (action !== 'accept' && action !== 'reject')) {
			return;
		}

		const properties = this.getBloomreachProperies(form, action, email);

		window.dataLayer.push(properties);

		if (window.exponea) {
			const exponeaProperties = {
				email: email,
				// eslint-disable-next-line camelcase
				email_id: email
			};

			if (properties?.event_properties?.first_name) {
				/* eslint-disable */
				exponeaProperties.first_name = properties.event_properties.first_name;
				/* eslint-enable */
			}

			if (properties?.event_properties?.last_name) {
				/* eslint-disable */
				exponeaProperties.last_name = properties.event_properties.last_name;
				/* eslint-enable */
			}

			window.exponea.update(exponeaProperties);
		}

		const userIsNotSubscribed = await this.returnBloomreachConsentStatus(email);
		if (!userIsNotSubscribed) {
			window.dataLayer.push({
				event: 'newsletter_subscribe'
			});
		}
	}

	getBloomreachProperies (form, action, email) {
		let dataSource = 'form';

		if (form.id === 'ocst-form') {
			dataSource = 'checkout form';
		}
		/* eslint-disable */
		const properties = {
			namespace: 'exponea',
			event_name: 'consent',
			event_properties: {
				action : action,
				category: 'email',
				email: email,
				data_source: dataSource,
				timestamp: Math.floor(Date.now() / 1000),
				valid_until: 'unlimited',
				location: window.location.href
			}
		};
		/* eslint-enable */

		const firstNameElem = form.elements.Customer_ShipFirstName;
		if (firstNameElem) {
			/* eslint-disable */
			properties.event_properties.first_name = firstNameElem.value;
			/* eslint-enable */
		}

		const lastNameElem = form.elements.Customer_ShipLastName;
		if (lastNameElem) {
			/* eslint-disable */
			properties.event_properties.last_name = lastNameElem.value;
			/* eslint-enable */
		}

		return properties;
	}

	async returnBloomreachConsentStatus (hardID) {
		const response = await this.checkBloomreachConsent(hardID, 'email');

		return response?.results?.[2]?.value === true;
	}
}
